import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  BarChart3, 
  PieChart, 
  TrendingUp, 
  TrendingDown,
  Users, 
  Building2, 
  DollarSign,
  Calendar,
  MapPin,
  Download,
  Target,
  Percent,
  Calculator,
  Award,
  AlertCircle
} from 'lucide-react';
import { enhancedOfficeData, locations } from '../data/locationData';

const TermRateReporting = ({ selectedLocation = 'All Locations' }) => {
  const [timeFrame, setTimeFrame] = useState('current');
  const [reportView, setReportView] = useState('overview');

  // Filter offices based on selected location
  const getFilteredOffices = () => {
    let offices = enhancedOfficeData.filter(office => office.status === 'LEASED' && office.signedRate > 0);
    
    if (selectedLocation !== 'All Locations') {
      offices = offices.filter(office => office.location === selectedLocation);
    }
    
    return offices;
  };

  const leasedOffices = getFilteredOffices();

  // Calculate term-based analytics
  const calculateTermAnalytics = () => {
    const termGroups = leasedOffices.reduce((acc, office) => {
      const term = office.leaseInfo?.termMonths || 0;
      const termCategory = term <= 3 ? '1-3 months' :
                          term <= 6 ? '4-6 months' :
                          term <= 12 ? '7-12 months' :
                          term <= 24 ? '13-24 months' :
                          '25+ months';
      
      if (!acc[termCategory]) {
        acc[termCategory] = {
          count: 0,
          offices: [],
          totalMarketedRevenue: 0,
          totalSignedRevenue: 0,
          avgMarketedRate: 0,
          avgSignedRate: 0,
          avgDiscount: 0,
          totalDesks: 0
        };
      }
      
      acc[termCategory].count++;
      acc[termCategory].offices.push(office);
      acc[termCategory].totalMarketedRevenue += office.marketedRates?.monthly || 0;
      acc[termCategory].totalSignedRevenue += office.signedRate;
      acc[termCategory].totalDesks += office.desks;
      
      return acc;
    }, {});

    // Calculate averages and discounts
    Object.keys(termGroups).forEach(term => {
      const group = termGroups[term];
      group.avgMarketedRate = group.totalMarketedRevenue / group.count;
      group.avgSignedRate = group.totalSignedRevenue / group.count;
      group.avgDiscount = ((group.avgMarketedRate - group.avgSignedRate) / group.avgMarketedRate * 100);
    });

    return termGroups;
  };

  // Calculate negotiation effectiveness
  const calculateNegotiationStats = () => {
    let totalSavings = 0;
    let negotiatedCount = 0;
    let fullRateCount = 0;
    let premiumCount = 0;

    leasedOffices.forEach(office => {
      const marketedRate = office.marketedRates?.monthly || 0;
      const signedRate = office.signedRate;
      
      if (marketedRate > 0 && signedRate > 0) {
        const difference = marketedRate - signedRate;
        totalSavings += Math.abs(difference);
        
        if (signedRate < marketedRate) {
          negotiatedCount++;
        } else if (signedRate === marketedRate) {
          fullRateCount++;
        } else {
          premiumCount++;
        }
      }
    });

    return {
      totalSavings,
      negotiatedCount,
      fullRateCount,
      premiumCount,
      avgSavings: totalSavings / leasedOffices.length,
      negotiationRate: (negotiatedCount / leasedOffices.length * 100)
    };
  };

  // Calculate revenue analysis by term
  const calculateRevenueByTerm = () => {
    const termAnalytics = calculateTermAnalytics();
    const totalMarketedRevenue = Object.values(termAnalytics).reduce((sum, group) => sum + group.totalMarketedRevenue, 0);
    const totalSignedRevenue = Object.values(termAnalytics).reduce((sum, group) => sum + group.totalSignedRevenue, 0);
    
    return {
      termAnalytics,
      totalMarketedRevenue,
      totalSignedRevenue,
      totalRevenueLoss: totalMarketedRevenue - totalSignedRevenue,
      overallDiscountRate: ((totalMarketedRevenue - totalSignedRevenue) / totalMarketedRevenue * 100)
    };
  };

  const termAnalytics = calculateTermAnalytics();
  const negotiationStats = calculateNegotiationStats();
  const revenueAnalysis = calculateRevenueByTerm();

  // Get top performers and opportunities
  const getTopPerformers = () => {
    const performers = leasedOffices.map(office => {
      const marketedRate = office.marketedRates?.monthly || 0;
      const signedRate = office.signedRate;
      const variance = signedRate - marketedRate;
      const variancePercent = marketedRate > 0 ? (variance / marketedRate * 100) : 0;
      
      return {
        ...office,
        variance,
        variancePercent,
        isNegotiated: signedRate !== marketedRate
      };
    }).sort((a, b) => b.variancePercent - a.variancePercent);

    return {
      topPremium: performers.filter(p => p.variance > 0).slice(0, 5),
      topDiscounted: performers.filter(p => p.variance < 0).slice(-5).reverse(),
      atMarketRate: performers.filter(p => p.variance === 0)
    };
  };

  const topPerformers = getTopPerformers();

  const exportReport = () => {
    alert('Term vs Rate report export functionality would be implemented here');
  };

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center space-x-3">
            <h1 className="text-3xl font-bold text-slate-800">Term vs Rate Analysis</h1>
            {selectedLocation !== 'All Locations' && (
              <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-300">
                <MapPin className="w-3 h-3 mr-1" />
                {selectedLocation}
              </Badge>
            )}
          </div>
          <p className="text-gray-600 mt-2">
            Analyze lease terms against marketed vs signed rates to understand negotiation patterns
          </p>
        </div>
        
        <div className="flex items-center space-x-3">
          <Select value={timeFrame} onValueChange={setTimeFrame}>
            <SelectTrigger className="w-40">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="current">Current Leases</SelectItem>
              <SelectItem value="quarter">This Quarter</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
              <SelectItem value="all">All Time</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={exportReport} variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Export Report
          </Button>
        </div>
      </div>

      {/* Key Metrics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border-gray-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Leased Offices</p>
                <p className="text-3xl font-bold text-slate-800">{leasedOffices.length}</p>
                <p className="text-xs text-gray-500 mt-1">Active leases analyzed</p>
              </div>
              <Building2 className="w-8 h-8 text-slate-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-gray-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Negotiation Rate</p>
                <p className="text-3xl font-bold text-amber-600">{negotiationStats.negotiationRate.toFixed(1)}%</p>
                <p className="text-xs text-gray-500 mt-1">
                  {negotiationStats.negotiatedCount} of {leasedOffices.length} leases
                </p>
              </div>
              <Percent className="w-8 h-8 text-amber-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-gray-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Avg Monthly Savings</p>
                <p className="text-3xl font-bold text-green-600">${negotiationStats.avgSavings.toFixed(0)}</p>
                <p className="text-xs text-gray-500 mt-1">Per negotiated lease</p>
              </div>
              <TrendingDown className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-gray-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Revenue Impact</p>
                <p className="text-3xl font-bold text-red-600">
                  {revenueAnalysis.overallDiscountRate >= 0 ? '-' : '+'}
                  {Math.abs(revenueAnalysis.overallDiscountRate).toFixed(1)}%
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  ${Math.abs(revenueAnalysis.totalRevenueLoss).toLocaleString()} monthly
                </p>
              </div>
              <DollarSign className="w-8 h-8 text-red-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Analysis Tabs */}
      <Tabs value={reportView} onValueChange={setReportView} className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Term Breakdown</TabsTrigger>
          <TabsTrigger value="negotiation">Negotiation Analysis</TabsTrigger>
          <TabsTrigger value="revenue">Revenue Impact</TabsTrigger>
          <TabsTrigger value="opportunities">Opportunities</TabsTrigger>
        </TabsList>

        {/* Term Breakdown Tab */}
        <TabsContent value="overview" className="space-y-6">
          <Card className="border-gray-200">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-slate-800">
                Lease Term Analysis
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(termAnalytics).map(([term, data]) => (
                  <div key={term} className="p-4 border rounded-lg bg-gray-50">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-slate-800">{term}</h4>
                      <Badge variant="outline" className="bg-white">
                        {data.count} lease{data.count !== 1 ? 's' : ''}
                      </Badge>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Avg Marketed Rate</span>
                        <p className="font-semibold text-slate-800">${data.avgMarketedRate.toFixed(0)}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Avg Signed Rate</span>
                        <p className="font-semibold text-emerald-600">${data.avgSignedRate.toFixed(0)}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Avg Discount</span>
                        <p className={`font-semibold ${data.avgDiscount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {data.avgDiscount >= 0 ? '-' : '+'}{Math.abs(data.avgDiscount).toFixed(1)}%
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-600">Total Revenue</span>
                        <p className="font-semibold text-slate-800">${data.totalSignedRevenue.toLocaleString()}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Total Desks</span>
                        <p className="font-semibold text-slate-800">{data.totalDesks}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Negotiation Analysis Tab */}
        <TabsContent value="negotiation" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-slate-800">
                  Negotiation Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-green-50 border border-green-200">
                    <div className="flex items-center">
                      <TrendingDown className="w-5 h-5 mr-3 text-green-600" />
                      <div>
                        <p className="font-medium text-green-800">Discounted Leases</p>
                        <p className="text-sm text-green-600">Below marketed rate</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-green-800">{negotiationStats.negotiatedCount}</p>
                      <p className="text-sm text-green-600">
                        {(negotiationStats.negotiatedCount / leasedOffices.length * 100).toFixed(1)}%
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-lg bg-blue-50 border border-blue-200">
                    <div className="flex items-center">
                      <Target className="w-5 h-5 mr-3 text-blue-600" />
                      <div>
                        <p className="font-medium text-blue-800">Market Rate Leases</p>
                        <p className="text-sm text-blue-600">At marketed rate</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-blue-800">{negotiationStats.fullRateCount}</p>
                      <p className="text-sm text-blue-600">
                        {(negotiationStats.fullRateCount / leasedOffices.length * 100).toFixed(1)}%
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-lg bg-amber-50 border border-amber-200">
                    <div className="flex items-center">
                      <TrendingUp className="w-5 h-5 mr-3 text-amber-600" />
                      <div>
                        <p className="font-medium text-amber-800">Premium Leases</p>
                        <p className="text-sm text-amber-600">Above marketed rate</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-amber-800">{negotiationStats.premiumCount}</p>
                      <p className="text-sm text-amber-600">
                        {(negotiationStats.premiumCount / leasedOffices.length * 100).toFixed(1)}%
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-slate-800">
                  Top Rate Variances
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <h5 className="font-medium text-green-800 mb-2">Highest Premium Rates</h5>
                    <div className="space-y-2">
                      {topPerformers.topPremium.slice(0, 3).map((office) => (
                        <div key={office.id} className="flex items-center justify-between p-2 rounded bg-green-50">
                          <span className="text-sm font-medium">{office.location} - {office.officeNumber}</span>
                          <span className="text-sm font-bold text-green-700">
                            +{office.variancePercent.toFixed(1)}%
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h5 className="font-medium text-red-800 mb-2">Highest Discounts</h5>
                    <div className="space-y-2">
                      {topPerformers.topDiscounted.slice(0, 3).map((office) => (
                        <div key={office.id} className="flex items-center justify-between p-2 rounded bg-red-50">
                          <span className="text-sm font-medium">{office.location} - {office.officeNumber}</span>
                          <span className="text-sm font-bold text-red-700">
                            {office.variancePercent.toFixed(1)}%
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Revenue Impact Tab */}
        <TabsContent value="revenue" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="border-gray-200 bg-gradient-to-br from-blue-50 to-indigo-50">
              <CardContent className="p-6 text-center">
                <Calculator className="w-8 h-8 mx-auto mb-3 text-blue-600" />
                <p className="text-sm text-blue-600 font-medium">Total Marketed Revenue</p>
                <p className="text-2xl font-bold text-blue-800">
                  ${revenueAnalysis.totalMarketedRevenue.toLocaleString()}
                </p>
                <p className="text-xs text-blue-600 mt-1">If all at marketed rates</p>
              </CardContent>
            </Card>

            <Card className="border-gray-200 bg-gradient-to-br from-emerald-50 to-green-50">
              <CardContent className="p-6 text-center">
                <DollarSign className="w-8 h-8 mx-auto mb-3 text-emerald-600" />
                <p className="text-sm text-emerald-600 font-medium">Actual Signed Revenue</p>
                <p className="text-2xl font-bold text-emerald-800">
                  ${revenueAnalysis.totalSignedRevenue.toLocaleString()}
                </p>
                <p className="text-xs text-emerald-600 mt-1">Current monthly revenue</p>
              </CardContent>
            </Card>

            <Card className="border-gray-200 bg-gradient-to-br from-red-50 to-rose-50">
              <CardContent className="p-6 text-center">
                <AlertCircle className="w-8 h-8 mx-auto mb-3 text-red-600" />
                <p className="text-sm text-red-600 font-medium">Revenue Variance</p>
                <p className="text-2xl font-bold text-red-800">
                  {revenueAnalysis.totalRevenueLoss >= 0 ? '-' : '+'}
                  ${Math.abs(revenueAnalysis.totalRevenueLoss).toLocaleString()}
                </p>
                <p className="text-xs text-red-600 mt-1">
                  {revenueAnalysis.overallDiscountRate.toFixed(1)}% vs marketed
                </p>
              </CardContent>
            </Card>
          </div>

          <Card className="border-gray-200">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-slate-800">
                Revenue Impact by Term Length
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left p-3 text-sm font-medium text-gray-600">Term Range</th>
                      <th className="text-center p-3 text-sm font-medium text-gray-600">Count</th>
                      <th className="text-right p-3 text-sm font-medium text-gray-600">Marketed Revenue</th>
                      <th className="text-right p-3 text-sm font-medium text-gray-600">Signed Revenue</th>
                      <th className="text-right p-3 text-sm font-medium text-gray-600">Variance</th>
                      <th className="text-center p-3 text-sm font-medium text-gray-600">Impact %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(termAnalytics).map(([term, data]) => {
                      const variance = data.totalSignedRevenue - data.totalMarketedRevenue;
                      const variancePercent = (variance / data.totalMarketedRevenue * 100);
                      return (
                        <tr key={term} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="p-3 font-medium text-slate-800">{term}</td>
                          <td className="p-3 text-center">{data.count}</td>
                          <td className="p-3 text-right font-medium">
                            ${data.totalMarketedRevenue.toLocaleString()}
                          </td>
                          <td className="p-3 text-right font-medium text-emerald-600">
                            ${data.totalSignedRevenue.toLocaleString()}
                          </td>
                          <td className={`p-3 text-right font-medium ${variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {variance >= 0 ? '+' : ''}${variance.toLocaleString()}
                          </td>
                          <td className={`p-3 text-center font-medium ${variancePercent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {variancePercent >= 0 ? '+' : ''}{variancePercent.toFixed(1)}%
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Opportunities Tab */}
        <TabsContent value="opportunities" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-slate-800 flex items-center">
                  <Award className="w-5 h-5 mr-2" />
                  Revenue Optimization Opportunities
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-4 rounded-lg bg-green-50 border border-green-200">
                    <h5 className="font-medium text-green-800 mb-2">Short-term Premium Potential</h5>
                    <p className="text-sm text-green-700 mb-2">
                      {Object.entries(termAnalytics).filter(([term, data]) => 
                        term.includes('1-3') && data.avgDiscount > 5
                      ).length} short-term leases with high discounts
                    </p>
                    <p className="text-xs text-green-600">
                      Consider premium pricing for flexibility on terms under 6 months
                    </p>
                  </div>

                  <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                    <h5 className="font-medium text-blue-800 mb-2">Long-term Incentive Analysis</h5>
                    <p className="text-sm text-blue-700 mb-2">
                      Average discount for 12+ month terms: {Object.entries(termAnalytics)
                        .filter(([term]) => term.includes('13-24') || term.includes('25+'))
                        .reduce((sum, [_, data]) => sum + data.avgDiscount, 0)
                        .toFixed(1)}%
                    </p>
                    <p className="text-xs text-blue-600">
                      Long-term discounts are driving tenant retention
                    </p>
                  </div>

                  <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
                    <h5 className="font-medium text-amber-800 mb-2">Market Rate Opportunities</h5>
                    <p className="text-sm text-amber-700 mb-2">
                      {negotiationStats.fullRateCount} leases at full market rate
                    </p>
                    <p className="text-xs text-amber-600">
                      {((negotiationStats.fullRateCount / leasedOffices.length) * 100).toFixed(1)}% of tenants accept standard pricing
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-slate-800">
                  Detailed Office Analysis
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {leasedOffices
                    .sort((a, b) => b.signedRate - (b.marketedRates?.monthly || 0) - (a.signedRate - (a.marketedRates?.monthly || 0)))
                    .slice(0, 10)
                    .map((office) => {
                      const variance = office.signedRate - (office.marketedRates?.monthly || 0);
                      const variancePercent = office.marketedRates?.monthly ? 
                        (variance / office.marketedRates.monthly * 100) : 0;
                      
                      return (
                        <div key={office.id} className="flex items-center justify-between p-3 rounded-lg border bg-white">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <p className="font-medium text-slate-800">
                                {office.location} - {office.officeNumber}
                              </p>
                              <Badge variant="outline" className="text-xs">
                                {office.leaseInfo?.termMonths}m
                              </Badge>
                            </div>
                            <div className="text-sm text-gray-600 mt-1">
                              <span>Marketed: ${office.marketedRates?.monthly?.toLocaleString()}</span>
                              <span className="mx-2">•</span>
                              <span>Signed: ${office.signedRate.toLocaleString()}</span>
                            </div>
                          </div>
                          <div className={`text-right ${variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            <p className="font-semibold">
                              {variance >= 0 ? '+' : ''}{variancePercent.toFixed(1)}%
                            </p>
                            <p className="text-xs">
                              {variance >= 0 ? '+' : ''}${variance.toLocaleString()}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TermRateReporting;